import React, {useCallback, useState} from "react";
import {Template} from "../../../components/template/template.jsx";
import {Card} from "../../../components/card/card.jsx";
import Button from "../../../components/button/button.jsx";
import InputForm from "../../../components/input/input-form.jsx";
import SelectComponent from "../../../components/select/select-component.jsx";
import SentryRow from "../../../components/template/sentry-row.jsx";
import ShowHideColumnsGrid from "../../../components/show-hide-columns-grid/show-hide-columns-grid.jsx";
import {AgGridReact} from "ag-grid-react";
import {keepPreviousData, useQuery} from "@tanstack/react-query";
import {FaEraser, FaPlus, FaQuestion, FaSearch} from "react-icons/fa";
import aggridUtils from "../../../utils/aggrid-utils.js";
import {DEFAULT_COL_DEF_NO_RESIZE_SUPPRESS_ALL} from "../../../utils/ag-grid-columns-utils.js";
import {navigateHook} from "../../../hooks/use-navigate.js";
import {useGlobalKey} from "../../../hooks/use-global-key.js";
import ScheduleGarrisonService from "../../../services/dispatch/schedule-garrison-service.js";

const API_DESPACHO_WEB = import.meta.env.VITE_API_DESPACHO_WEB;

const STATUS_OPTIONS = [
    {value: "Ativo", label: "Ativo"},
    {value: "Disponível em", label: "Disponível em"},
    {value: "Encerrado", label: "Encerrado"},
    {value: "Inativo", label: "Inativo"},
];

const initialFilters = {status: null, name: "", containedText: ""};

const buildFilter = (values) => [
    {field: "status", type: "=", value: values.status?.value ?? ""},
    {field: "name", type: "like", value: (values.name ?? "").replaceAll("'", "")},
    {field: "containedText", type: "like", value: (values.containedText ?? "").replaceAll("'", "")},
];

const HIDDEN_COLUMNS_STORAGE = "schedule-garrison-hidden-columns";
const DEFAULT_HIDDEN_COLUMNS = ["id"];

const baseColumns = [
    {field: "id", headerName: "ID", width: 90, cellDataType: "number"},
    {field: "status", headerName: "Status", width: 220},
    {field: "name", headerName: "Nome", flex: 1},
];

const buildColumnDefs = () => {
    const hiddenColumns = JSON.parse(localStorage.getItem(HIDDEN_COLUMNS_STORAGE)) ?? DEFAULT_HIDDEN_COLUMNS;
    return baseColumns.map(column => ({...column, hide: hiddenColumns.includes(column.field)}));
};

const ScheduleGarrisonList = () => {
    const navigate = navigateHook();
    const [filterValues, setFilterValues] = useState(initialFilters);
    const [filter, setFilter] = useState([]);

    const {data = [], isLoading} = useQuery({
        queryFn: () => ScheduleGarrisonService.list({filter: [...filter]}),
        queryKey: ["dispatch-schedule-garrison", {filter}],
        placeholderData: keepPreviousData,
    });

    const [columnDefs, setColumnDefs] = useState(buildColumnDefs);

    const toggleColumn = (field) => {
        setColumnDefs(prev => prev.map(column => column.field === field ? {...column, hide: !column.hide} : column));
    };

    const onColumnVisible = ({source, column}) => {
        if (source === "uiColumnMoved") toggleColumn(column.colDef.field);
    };

    const handleInput = ({target: {id, value}}) => setFilterValues(prev => ({...prev, [id]: value}));

    const apply = useCallback(() => setFilter(buildFilter(filterValues)), [filterValues]);

    const clear = useCallback(() => {
        setFilterValues(initialFilters);
        setFilter([]);
    }, []);

    const onRowClicked = ({data: row}) => {
        if (!row?.id) return;
        navigate(`${API_DESPACHO_WEB}/schedule-garrison/form/${row.id}`, true);
    };

    const openForm = () => navigate(API_DESPACHO_WEB + "/schedule-garrison/form", false);

    useGlobalKey("Enter", apply);
    useGlobalKey("Escape", clear);

    return (
        <Template isLoading={isLoading}>
            <Template.Header>
                <Template.Title>Atividades programadas</Template.Title>
                <Button onClick={openForm}><FaPlus/>Adicionar atividade programada</Button>
            </Template.Header>
            <Template.Body className="flex flex-col space-y-2">
                <Card>
                    <Card.Body>
                        <SentryRow className="items-start">
                            <SelectComponent classNameWrapper="col-span-3" label="Status" options={STATUS_OPTIONS}
                                             value={filterValues.status}
                                             onChange={(option) => setFilterValues(prev => ({...prev, status: option}))}/>
                            <InputForm classNameWrapper="col-span-4" id="name" label="Nome" value={filterValues.name} onChange={handleInput}/>
                            <div className="col-span-5 flex justify-between gap-4">
                                <InputForm
                                    classNameWrapper="w-full"
                                    id="containedText"
                                    label="Texto contido"
                                    inputClass="pr-9"
                                    title="Filtros: Descrição"
                                    value={filterValues.containedText}
                                    onChange={handleInput}
                                    span={
                                        <span
                                            className="absolute right-2 top-1/2 z-20 flex size-5 -translate-y-1/2 items-center justify-center rounded-full bg-[#E9ECEF] text-[#676a6c]"
                                            title="Filtros: Descrição"
                                            aria-label="Campos pesquisados pelo filtro Texto contido"
                                        >
                                            <FaQuestion size={10}/>
                                        </span>
                                    }
                                />
                                <div className="flex gap-2 justify-end">
                                    <Button onClick={clear} title="Limpar" target="primary-outline"><FaEraser/></Button>
                                    <Button onClick={apply} title="Buscar"><FaSearch/></Button>
                                </div>
                            </div>
                        </SentryRow>
                    </Card.Body>
                </Card>
                <Card>
                    <Card.Body>
                        <div className="h-[calc(100vh-153px)] aggrid-row-pointer">
                            <AgGridReact
                                rowData={data}
                                columnDefs={columnDefs}
                                defaultColDef={DEFAULT_COL_DEF_NO_RESIZE_SUPPRESS_ALL}
                                pagination
                                onRowClicked={onRowClicked}
                                onColumnVisible={onColumnVisible}
                                paginationPageSize={100}
                                paginationPageSizeSelector={[20, 50, 100, 500]}
                                animateRows={false}
                                suppressCellFocus={true}
                                gridOptions={{localeText: aggridUtils.localization()}}
                                theme={aggridUtils.generalTheme()}
                            />
                        </div>

                        <ShowHideColumnsGrid
                            columnDefs={columnDefs}
                            toggleColumn={toggleColumn}
                            storageItem={HIDDEN_COLUMNS_STORAGE}
                        />
                    </Card.Body>
                </Card>
            </Template.Body>
        </Template>
    );
};

export default ScheduleGarrisonList;
